(function (factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else api.start(window);
})(function () {
  'use strict';
  const MANUAL_KEY = 'casa:localization:manual:v1';
  const VISIT_KEY = 'casa:localization:visited:v1';
  const MANUAL_LIFETIME = 365 * 24 * 60 * 60 * 1000;
  const normalize = value => String(value || '').toLowerCase();
  const matchLanguage = (language, available) => available.find(code => normalize(code) === normalize(language))
    || available.find(code => normalize(code).split('-')[0] === normalize(language).split('-')[0]);

  function chooseLocalization(config, response, preferences) {
    const parts = response?.suggestions?.[0]?.parts || {};
    const country = config.countries.includes(parts.country?.handle) ? parts.country.handle : config.country;
    let language = matchLanguage(parts.language?.handle, config.languages);
    if (!language) {
      for (const preference of preferences || []) {
        language = matchLanguage(preference, config.languages);
        if (language) break;
      }
    }
    language = language || config.language;
    return {country, language, changed: country !== config.country || normalize(language) !== normalize(config.language)};
  }

  async function start(win) {
    const doc = win.document;
    const status = value => { doc.documentElement.dataset.casaLocalization = value; };
    const read = (storage, key) => { try { return win[storage].getItem(key); } catch (_) { return null; } };
    const write = (storage, key, value) => { try { win[storage].setItem(key, value); } catch (_) {} };
    let manual = false;
    // Explicit choices win, including choosing English while the browser prefers another language.
    doc.addEventListener('submit', event => {
      if (!event.target.matches('.casa-localization-form')) return;
      manual = true;
      const choice = {at:Date.now(), country:config?.country, language:config?.language};
      if (event.submitter?.name === 'country_code') choice.country = event.submitter.value;
      if (event.submitter?.name === 'language_code') choice.language = event.submitter.value;
      write('localStorage', MANUAL_KEY, JSON.stringify(choice));
    }, true);
    const configElement = doc.getElementById('CasaAutoLocalizationConfig');
    const form = doc.getElementById('CasaAutoLocalizationForm');
    if (!configElement || !form) return;
    let config;
    try { config = JSON.parse(configElement.textContent); } catch (_) { status('unavailable'); return; }
    const query = new URLSearchParams(win.location.search);
    const preview = query.has('preview_theme_id');
    const crawler = /bot\b|crawler|spider|google-inspectiontool|facebookexternalhit|mediapartners/i.test(win.navigator.userAgent || '');
    if (config.designMode || win.Shopify?.designMode || preview || crawler || !['index','product','collection','search','page','blog','article'].includes(config.pageType)) {
      status('not-applicable'); return;
    }
    // Shopping links must keep their submitted language/currency for visitors in every location.
    // The feed's explicit market, or a paid Google click, takes precedence over inferred preferences.
    if (['country','currency','gclid','gbraid','wbraid'].some(key => query.has(key)) || query.get('utm_source') === 'google') {
      status('fixed-ad-landing'); return;
    }
    // Never redirect an explicitly localized URL, or override an established visit.
    if ((config.rootUrl || '/').replace(/\/$/, '') !== '') { status('localized-url'); return; }
    const submit = (target, state) => {
      form.elements.namedItem('country_code').value = target.country;
      form.elements.namedItem('language_code').value = target.language;
      // Shopify applies the language prefix while retaining the page, variant, campaign parameters, and anchor.
      form.elements.namedItem('return_to').value = win.location.pathname + win.location.search + win.location.hash;
      status(state);
      form.submit();
    };
    let savedChoice;
    const saved = read('localStorage', MANUAL_KEY);
    try { savedChoice = JSON.parse(saved); } catch (_) {}
    const manualAt = Number(savedChoice?.at || saved);
    if (manualAt > 0 && Date.now() - manualAt < MANUAL_LIFETIME) {
      const target = {
        country:config.countries.includes(savedChoice?.country) ? savedChoice.country : config.country,
        language:matchLanguage(savedChoice?.language, config.languages) || config.language
      };
      if (target.country !== config.country || normalize(target.language) !== normalize(config.language)) submit(target, 'manual-redirecting');
      else status('manual');
      return;
    }
    if (read('sessionStorage', VISIT_KEY)) { status('session'); return; }
    write('sessionStorage', VISIT_KEY, '1');
    status('checking');
    const preferences = win.navigator.languages?.length ? [...win.navigator.languages] : [win.navigator.language];
    const controller = typeof win.AbortController === 'function' ? new win.AbortController() : null;
    const timer = controller ? win.setTimeout(() => controller.abort(), 3500) : null;
    let response = {};
    try {
      const endpoint = new URL('browsing_context_suggestions.json', win.location.origin + (config.rootUrl.endsWith('/') ? config.rootUrl : config.rootUrl + '/'));
      endpoint.searchParams.set('country[enabled]', 'true');
      endpoint.searchParams.set('country[exclude]', config.country);
      endpoint.searchParams.set('language[enabled]', 'true');
      endpoint.searchParams.set('language[exclude]', config.language);
      const result = await win.fetch(endpoint.href, {credentials:'same-origin', headers:{Accept:'application/json'}, ...(controller ? {signal:controller.signal} : {})});
      if (result.ok) response = await result.json();
    } catch (_) {
      // A temporary suggestions failure must not break shopping; browser language still works.
    } finally { if (timer !== null) win.clearTimeout(timer); }
    if (manual) { status('manual'); return; }
    const target = chooseLocalization(config, response, preferences);
    if (!target.changed) { status('not-needed'); return; }
    submit(target, 'redirecting');
  }
  return {chooseLocalization, start, MANUAL_KEY, VISIT_KEY};
});
